import { useEffect, useState } from "react";

const QUERY = "(any-pointer: fine)";

/**
 * True when the device has ANY fine pointer (mouse or trackpad) — the gate for
 * showing pointer/keyboard-shortcut affordances like "⌘ + click" hints or the
 * ⌘K badge. `any-pointer` (not `pointer`) so an iPad with a Magic Keyboard
 * trackpad qualifies: those shortcuts genuinely work there, and hiding them by
 * platform would take them from the setup that uses them most. A touch-only
 * device reports false and the hint is noise it can't act on.
 *
 * Initial state comes from a synchronous matchMedia read (not an effect), so
 * the first paint is already correct and hints don't flash in or out.
 */
export function useHasFinePointer(): boolean {
  const [finePointer, setFinePointer] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia(QUERY).matches,
  );

  useEffect(() => {
    const mql = window.matchMedia(QUERY);
    const onChange = () => setFinePointer(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return finePointer;
}
